import type { ErrorObject } from "ajv";
import {
  validateApplication as validateApplicationStandalone,
  validateUx as validateUxStandalone,
} from "./generated/library-composition-validators";
import {
  isApplicationWebMcpMode,
  parseApplicationWebMcpToolRef,
  validateApplicationBlueprintRuntimeCapabilities,
} from "@cogo/types-v2";
import type {
  ApplicationBlueprint,
  ApplicationCapabilityRequirement,
  LibraryAdmissionResult,
  LibraryAuthorityAdmissionResult,
  LibraryAuthorityContext,
  LibraryCompositionInput,
  LibraryGap,
  LibraryGapCode,
  LibraryPort,
  LibraryPortEndpoint,
  ProjectValidationLibraryAuthorityContext,
  UxPatternDefinition,
} from "./types";

const PROJECT_CONTRACT_REF =
  "cogo:project-validation-contract/wonbanking-account-selection-v1";
const PROJECT_CORE_REF =
  "cogo:project-validation-application-core/wonbanking-account-selection-v1";
const PROJECT_AGGREGATION_SCOPE = "wonbanking-library-validation";
const PROJECT_SLICE_ID = "account-selection-bottom-sheet";
const SHA256_PATTERN = /^sha256:[a-f0-9]{64}$/;

// `validateApplication` and `validateUx` are PRECOMPILED at build time, because
// Ajv's `.compile()` generates validator code via `new Function(...)` and the
// production Pages CSP does not grant `'unsafe-eval'`. Deferring the compile to
// first call — what this module did before — protected the IMPORT, which is
// what the 2026-07-25 white-screen outage needed, but left the CALL throwing in
// the page. Regenerate with
// `pnpm --filter @cogo/agentic-generator generate:composition-admission-validators`.
// See src/__tests__/no-module-scope-codegen.test.ts.
//
// The generated module is `@ts-nocheck`, so its exports carry no Ajv typing.
// This is the same local shape `packages/cogo-react-renderer/src/document-model/
// document-model-admission.ts` declares for its standalone validator.
type StandaloneValidateFunction = ((value: unknown) => boolean) & {
  errors?: ErrorObject[] | null;
};

const validateApplication =
  validateApplicationStandalone as unknown as StandaloneValidateFunction;
const validateUx = validateUxStandalone as unknown as StandaloneValidateFunction;

const instanceIdPattern = /^[a-z][a-z0-9-]*$/;
const requestHashPattern = /^sha256:[0-9a-f]{64}$/;
const MAX_REQUIREMENT_EVIDENCE_REF_LENGTH = 512;
const invalidSnapshot = Symbol("invalid-library-snapshot");
const LIBRARY_SNAPSHOT_LIMITS = Object.freeze({
  maxDepth: 64,
  maxNodes: 1_024,
  maxProperties: 8_192,
  maxLexicalSize: 524_288,
});

interface LibrarySnapshotState {
  active: WeakSet<object>;
  seen: WeakSet<object>;
  nodes: number;
  properties: number;
  lexicalSize: number;
  blocked: boolean;
}

export function admitLibraryComposition(
  candidate: unknown,
  authorityContext: unknown,
): LibraryAdmissionResult {
  const candidateSnapshot = snapshotLibraryCandidate(candidate);
  if (!candidateSnapshot.ok) return candidateSnapshot;

  const authorityAdmission = admitLibraryAuthorityContext(authorityContext);
  if (!authorityAdmission.ok) return authorityAdmission;

  const schemaGaps = validateUnknownInput(candidateSnapshot.value);
  if (schemaGaps.length > 0) return { ok: false, gaps: schemaGaps };

  const input = candidateSnapshot.value as LibraryCompositionInput;
  const policyGaps = evaluateLibraryPolicy(
    input,
    authorityAdmission.authorityContext,
  );
  return policyGaps.length > 0
    ? { ok: false, gaps: policyGaps }
    : { ok: true, input };
}

export function admitLibraryAuthorityContext(
  authorityContext: unknown,
): LibraryAuthorityAdmissionResult {
  try {
    const authoritySnapshot = snapshotAuthorityContext(authorityContext);
    const gaps = validateAuthorityContext(authoritySnapshot);
    return gaps.length > 0
      ? { ok: false, gaps }
      : {
          ok: true,
          authorityContext:
            authoritySnapshot as Readonly<LibraryAuthorityContext>,
        };
  } catch {
    return {
      ok: false,
      gaps: [
        gap(
          "library-lineage-missing",
          "/authorityContext",
          "Library authority context must be readable and well formed",
        ),
      ],
    };
  }
}

function snapshotLibraryCandidate(
  candidate: unknown,
):
  | { ok: true; value: unknown }
  | { ok: false; gaps: LibraryGap[] } {
  const gaps: LibraryGap[] = [];
  const state: LibrarySnapshotState = {
    active: new WeakSet(),
    seen: new WeakSet(),
    nodes: 0,
    properties: 0,
    lexicalSize: 0,
    blocked: false,
  };
  const value = snapshotJsonValue(candidate, "", 0, state, gaps);
  if (value === invalidSnapshot || gaps.length > 0) {
    return {
      ok: false,
      gaps: dedupe(gaps).sort(
        (left, right) =>
          compareText(left.path, right.path) ||
          compareText(left.code, right.code) ||
          compareText(left.message, right.message),
      ),
    };
  }
  return { ok: true, value };
}

function snapshotJsonValue(
  value: unknown,
  path: string,
  depth: number,
  state: LibrarySnapshotState,
  gaps: LibraryGap[],
): unknown | typeof invalidSnapshot {
  if (state.blocked) return invalidSnapshot;
  if (depth > LIBRARY_SNAPSHOT_LIMITS.maxDepth)
    return snapshotBudgetExceeded(
      state,
      gaps,
      path,
      `Library input exceeds snapshot depth limit of ${LIBRARY_SNAPSHOT_LIMITS.maxDepth}`,
    );

  if (value === null) {
    if (!consumeSnapshotLexicalSize(4, path, state, gaps))
      return invalidSnapshot;
    return value;
  }
  if (typeof value === "string") {
    if (!consumeSnapshotLexicalSize(value.length, path, state, gaps))
      return invalidSnapshot;
    return value;
  }
  if (typeof value === "boolean") {
    if (
      !consumeSnapshotLexicalSize(value ? 4 : 5, path, state, gaps)
    )
      return invalidSnapshot;
    return value;
  }
  if (typeof value === "number") {
    if (Number.isFinite(value)) {
      if (
        !consumeSnapshotLexicalSize(
          String(value).length,
          path,
          state,
          gaps,
        )
      )
        return invalidSnapshot;
      return value;
    }
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input values must be JSON compatible",
      ),
    );
    return invalidSnapshot;
  }
  if (typeof value !== "object") {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input values must be JSON compatible",
      ),
    );
    return invalidSnapshot;
  }
  if (state.active.has(value)) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input must be acyclic",
      ),
    );
    return invalidSnapshot;
  }
  if (state.seen.has(value)) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input may not reuse object identity",
      ),
    );
    state.blocked = true;
    return invalidSnapshot;
  }

  state.seen.add(value);
  state.nodes += 1;
  if (state.nodes > LIBRARY_SNAPSHOT_LIMITS.maxNodes)
    return snapshotBudgetExceeded(
      state,
      gaps,
      path,
      `Library input exceeds snapshot node limit of ${LIBRARY_SNAPSHOT_LIMITS.maxNodes}`,
    );

  state.active.add(value);
  try {
    const array = Array.isArray(value);
    const prototype = Reflect.getPrototypeOf(value);
    if (
      (array && prototype !== Array.prototype) ||
      (!array && prototype !== Object.prototype && prototype !== null)
    ) {
      gaps.push(
        gap(
          "library-input-schema-invalid",
          path,
          "Library input objects must be plain JSON containers",
        ),
      );
      return invalidSnapshot;
    }

    const keys = Reflect.ownKeys(value);
    state.properties += keys.length;
    if (state.properties > LIBRARY_SNAPSHOT_LIMITS.maxProperties)
      return snapshotBudgetExceeded(
        state,
        gaps,
        path,
        `Library input exceeds snapshot property limit of ${LIBRARY_SNAPSHOT_LIMITS.maxProperties}`,
      );
    if (keys.some((key) => typeof key === "symbol")) {
      gaps.push(
        gap(
          "library-input-schema-invalid",
          path,
          "Library input values must be JSON compatible",
        ),
      );
      return invalidSnapshot;
    }
    const stringKeys = (keys as string[]).sort();
    if (
      !consumeSnapshotLexicalSize(
        stringKeys.reduce((total, key) => total + key.length, 0),
        path,
        state,
        gaps,
      )
    )
      return invalidSnapshot;
    return array
      ? snapshotJsonArray(
          value,
          path,
          stringKeys,
          depth,
          state,
          gaps,
        )
      : snapshotJsonObject(
          value,
          path,
          stringKeys,
          depth,
          state,
          gaps,
        );
  } catch {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input must be readable as plain JSON data",
      ),
    );
    return invalidSnapshot;
  } finally {
    state.active.delete(value);
  }
}

function snapshotJsonObject(
  value: object,
  path: string,
  keys: string[],
  depth: number,
  state: LibrarySnapshotState,
  gaps: LibraryGap[],
) {
  const snapshot: Record<string, unknown> = {};
  for (const key of keys) {
    if (state.blocked) break;
    const propertyPath = jsonPath(path, key);
    const descriptor = readJsonDataDescriptor(
      value,
      key,
      propertyPath,
      gaps,
    );
    if (!descriptor) continue;
    const child = snapshotJsonValue(
      descriptor.value,
      propertyPath,
      depth + 1,
      state,
      gaps,
    );
    if (child === invalidSnapshot) continue;
    Object.defineProperty(snapshot, key, {
      value: child,
      enumerable: true,
      configurable: false,
      writable: false,
    });
  }
  return Object.freeze(snapshot);
}

function snapshotJsonArray(
  value: object,
  path: string,
  keys: string[],
  depth: number,
  state: LibrarySnapshotState,
  gaps: LibraryGap[],
) {
  const lengthDescriptor = readJsonDataDescriptor(
    value,
    "length",
    path,
    gaps,
    false,
  );
  const length = lengthDescriptor?.value;
  if (
    typeof length !== "number" ||
    !Number.isSafeInteger(length) ||
    length < 0
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input arrays must have a valid length",
      ),
    );
    return invalidSnapshot;
  }

  const indexKeys = keys.filter((key) => key !== "length");
  const validIndexKeys: string[] = [];
  for (const key of indexKeys) {
    const index = Number(key);
    if (
      !/^(0|[1-9][0-9]*)$/.test(key) ||
      !Number.isSafeInteger(index) ||
      index >= length
    ) {
      gaps.push(
        gap(
          "library-input-schema-invalid",
          jsonPath(path, key),
          "Library input arrays may contain only indexed JSON values",
        ),
      );
    } else {
      validIndexKeys.push(key);
    }
  }
  if (validIndexKeys.length !== length) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input arrays must not be sparse",
      ),
    );
    return invalidSnapshot;
  }

  const snapshot: unknown[] = [];
  validIndexKeys.sort((left, right) => Number(left) - Number(right));
  for (const key of validIndexKeys) {
    if (state.blocked) break;
    const index = Number(key);
    const propertyPath = jsonPath(path, key);
    const descriptor = readJsonDataDescriptor(
      value,
      key,
      propertyPath,
      gaps,
    );
    if (!descriptor) continue;
    const child = snapshotJsonValue(
      descriptor.value,
      propertyPath,
      depth + 1,
      state,
      gaps,
    );
    if (child !== invalidSnapshot) snapshot[index] = child;
  }
  return Object.freeze(snapshot);
}

function consumeSnapshotLexicalSize(
  amount: number,
  path: string,
  state: LibrarySnapshotState,
  gaps: LibraryGap[],
) {
  state.lexicalSize += amount;
  if (state.lexicalSize <= LIBRARY_SNAPSHOT_LIMITS.maxLexicalSize)
    return true;
  snapshotBudgetExceeded(
    state,
    gaps,
    path,
    `Library input exceeds snapshot lexical size limit of ${LIBRARY_SNAPSHOT_LIMITS.maxLexicalSize}`,
  );
  return false;
}

function snapshotBudgetExceeded(
  state: LibrarySnapshotState,
  gaps: LibraryGap[],
  path: string,
  message: string,
) {
  if (!state.blocked)
    gaps.push(
      gap("library-input-schema-invalid", path, message),
    );
  state.blocked = true;
  return invalidSnapshot;
}

function readJsonDataDescriptor(
  value: object,
  key: string,
  path: string,
  gaps: LibraryGap[],
  requireEnumerable = true,
): PropertyDescriptor | undefined {
  const descriptor = Reflect.getOwnPropertyDescriptor(value, key);
  if (
    !descriptor ||
    !("value" in descriptor) ||
    (requireEnumerable && !descriptor.enumerable)
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        path,
        "Library input properties must be plain data properties",
      ),
    );
    return undefined;
  }
  return descriptor;
}

function jsonPath(parent: string, key: string) {
  const escaped = key.replace(/~/g, "~0").replace(/\//g, "~1");
  return `${parent}/${escaped}`;
}

function compareText(left: string, right: string) {
  return left < right ? -1 : left > right ? 1 : 0;
}

function snapshotAuthorityContext(candidate: unknown): unknown {
  const plain = snapshotLibraryCandidate(candidate);
  if (!plain.ok) throw new Error("Authority input is not stable plain JSON");
  if (!isRecord(plain.value)) return plain.value;

  const snapshot = Object.create(null) as Record<string, unknown>;
  const keys = Reflect.ownKeys(plain.value) as string[];
  for (const key of keys.sort()) {
    const value = plain.value[key];
    const refs = Array.isArray(value) ? [...value] : [];
    const normalized =
      Array.isArray(value) &&
      refs.every((ref) => typeof ref === "string")
        ? Object.freeze([...refs].sort())
        : value;
    Object.defineProperty(snapshot, key, {
      value: normalized,
      enumerable: true,
      configurable: false,
      writable: false,
    });
  }
  return Object.freeze(snapshot);
}

function validateAuthorityContext(candidate: unknown): LibraryGap[] {
  if (!isRecord(candidate)) {
    return [
      gap(
        "library-lineage-missing",
        "/authorityContext",
        "Library authority context must be supplied independently",
      ),
    ];
  }
  const canonicalKeys = [
    "admittedUxResearchRefs",
    "approvedCanonicalSourceRefs",
    "admittedApplicationCoreRefs",
  ] as const;
  const projectKeys = [
    ...canonicalKeys,
    "approvedProjectValidationContractRefs",
    "approvedProjectValidationCoreRefs",
    "aggregationScope",
    "sliceId",
    "contractHash",
    "nonPromotion",
  ] as const;
  const refPatterns: Record<(typeof canonicalKeys)[number], RegExp> = {
    admittedUxResearchRefs: /^cogo:ux-observation\/[a-z0-9][a-z0-9-]*$/,
    approvedCanonicalSourceRefs:
      /^cogo:approved-source\/[a-z0-9][a-z0-9-]*$/,
    admittedApplicationCoreRefs:
      /^cogo:application-core\/[a-z0-9][a-z0-9-]*$/,
  };
  const ownKeys = Reflect.ownKeys(candidate);
  const hasExactCanonicalKeys =
    ownKeys.length === canonicalKeys.length &&
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        canonicalKeys.includes(key as (typeof canonicalKeys)[number]),
    );
  const hasExactCanonicalData = canonicalKeys.every((key) => {
    const descriptor = Reflect.getOwnPropertyDescriptor(candidate, key);
    if (
      !descriptor ||
      !("value" in descriptor) ||
      !descriptor.enumerable ||
      !Array.isArray(descriptor.value)
    )
      return false;
    return descriptor.value.every(
      (value: unknown) =>
        typeof value === "string" && refPatterns[key].test(value),
    );
  });
  if (hasExactCanonicalKeys && hasExactCanonicalData) return [];

  const hasExactProjectKeys =
    ownKeys.length === projectKeys.length &&
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        projectKeys.includes(key as (typeof projectKeys)[number]),
    );
  const exactArray = (key: string, expected: readonly string[]) => {
    const descriptor = Reflect.getOwnPropertyDescriptor(candidate, key);
    return (
      descriptor &&
      "value" in descriptor &&
      descriptor.enumerable &&
      Array.isArray(descriptor.value) &&
      descriptor.value.length === expected.length &&
      descriptor.value.every(
        (value: unknown, index: number) => value === expected[index],
      )
    );
  };
  const exactValue = (key: string, expected: unknown) => {
    const descriptor = Reflect.getOwnPropertyDescriptor(candidate, key);
    return (
      descriptor &&
      "value" in descriptor &&
      descriptor.enumerable &&
      descriptor.value === expected
    );
  };
  const contractHash = Reflect.getOwnPropertyDescriptor(
    candidate,
    "contractHash",
  );
  const hasExactProjectData =
    canonicalKeys.every((key) => exactArray(key, [])) &&
    exactArray("approvedProjectValidationContractRefs", [
      PROJECT_CONTRACT_REF,
    ]) &&
    exactArray("approvedProjectValidationCoreRefs", [PROJECT_CORE_REF]) &&
    exactValue("aggregationScope", PROJECT_AGGREGATION_SCOPE) &&
    exactValue("sliceId", PROJECT_SLICE_ID) &&
    exactValue("nonPromotion", true) &&
    Boolean(
      contractHash &&
        "value" in contractHash &&
        contractHash.enumerable &&
        typeof contractHash.value === "string" &&
        SHA256_PATTERN.test(contractHash.value),
    );
  if (!hasExactProjectKeys || !hasExactProjectData) {
    return [
      gap(
        "library-lineage-missing",
        "/authorityContext",
        "Library authority context must contain exact admitted reference lists",
      ),
    ];
  }
  return [];
}

function isProjectAuthorityContext(
  authority: LibraryAuthorityContext,
): authority is ProjectValidationLibraryAuthorityContext {
  return "approvedProjectValidationContractRefs" in authority;
}

function validateUnknownInput(candidate: unknown): LibraryGap[] {
  if (!isRecord(candidate)) {
    return [
      gap(
        "library-input-schema-invalid",
        "",
        "Library input must be an object",
      ),
    ];
  }

  const envelopeKeys = [
    "applicationInstanceId",
    "applicationBlueprint",
    "uxPatterns",
    "capabilityRequirement",
  ];
  const unknownEnvelopeKeys = Object.keys(candidate).filter(
    (key) => !envelopeKeys.includes(key),
  );
  if (unknownEnvelopeKeys.length > 0) {
    return unknownEnvelopeKeys.map((key) =>
      gap(
        "library-input-schema-invalid",
        `/${key}`,
        "Library input has an unknown root property",
      ),
    );
  }

  const gaps: LibraryGap[] = [];
  if (
    typeof candidate.applicationInstanceId !== "string" ||
    !instanceIdPattern.test(candidate.applicationInstanceId)
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        "/applicationInstanceId",
        "applicationInstanceId must be an exact identifier",
      ),
    );
  }

  if (!isRecord(candidate.applicationBlueprint)) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        "/applicationBlueprint",
        "applicationBlueprint must be an object",
      ),
    );
  } else if (!validateApplication(candidate.applicationBlueprint)) {
    gaps.push(
      ...mapSchemaErrors(validateApplication.errors, "/applicationBlueprint"),
    );
  }

  if (!Array.isArray(candidate.uxPatterns)) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        "/uxPatterns",
        "uxPatterns must be an array",
      ),
    );
  } else {
    candidate.uxPatterns.forEach((ux, index) => {
      if (!validateUx(ux))
        gaps.push(
          ...mapSchemaErrors(validateUx.errors, `/uxPatterns/${index}`),
        );
    });
  }

  gaps.push(...validateCapabilityRequirementShape(candidate.capabilityRequirement));

  forEachEndpoint(candidate.applicationBlueprint, (value, path) => {
    if (hasLocalReference(value)) {
      gaps.push(
        gap(
          "cross-scope-local-reference",
          path,
          "Endpoint fields may not reference another local scope",
        ),
      );
    }
  });

  return dedupe(gaps);
}

function validateCapabilityRequirementShape(candidate: unknown): LibraryGap[] {
  const path = "/capabilityRequirement";
  if (!isRecord(candidate) || Array.isArray(candidate)) {
    return [
      gap(
        "library-input-schema-invalid",
        path,
        "capabilityRequirement must be an object",
      ),
    ];
  }

  const keys = [
    "schemaVersion",
    "requestedWebMcpMode",
    "requestHash",
    "requirementEvidenceRefs",
  ];
  const unknownKeys = Object.keys(candidate).filter(
    (key) => !keys.includes(key),
  );
  const gaps: LibraryGap[] = unknownKeys.map((key) =>
    gap(
      "library-input-schema-invalid",
      `${path}/${key}`,
      "capabilityRequirement has an unknown property",
    ),
  );

  if (
    candidate.schemaVersion !== "cogo.application-capability-requirement.v1"
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        `${path}/schemaVersion`,
        "capabilityRequirement must declare the exact capability requirement schema version",
      ),
    );
  }
  if (!isApplicationWebMcpMode(candidate.requestedWebMcpMode)) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        `${path}/requestedWebMcpMode`,
        "requestedWebMcpMode must be an exact application WebMCP mode",
      ),
    );
  }
  if (
    typeof candidate.requestHash !== "string" ||
    !requestHashPattern.test(candidate.requestHash)
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        `${path}/requestHash`,
        "requestHash must be an exact sha256 request digest",
      ),
    );
  }
  if (
    !Array.isArray(candidate.requirementEvidenceRefs) ||
    candidate.requirementEvidenceRefs.some((ref) => typeof ref !== "string")
  ) {
    gaps.push(
      gap(
        "library-input-schema-invalid",
        `${path}/requirementEvidenceRefs`,
        "requirementEvidenceRefs must be an array of opaque evidence references",
      ),
    );
  }
  return gaps;
}

function evaluateLibraryPolicy(
  input: LibraryCompositionInput,
  authority: LibraryAuthorityContext,
): LibraryGap[] {
  const gaps: LibraryGap[] = [];
  const app = input.applicationBlueprint;
  const definitions = new Map<string, UxPatternDefinition>();

  input.uxPatterns.forEach((definition, index) => {
    const ref = `${definition.uxPatternId}@${definition.version}`;
    if (definitions.has(ref)) {
      gaps.push(
        gap(
          "duplicate-ux-pattern-ref",
          `/uxPatterns/${index}`,
          "UX definition references must be unique",
        ),
      );
    } else definitions.set(ref, definition);
    evaluateDefinition(definition, `/uxPatterns/${index}`, gaps);
    evaluateUxAuthority(
      definition,
      `/uxPatterns/${index}/lineage`,
      authority,
      gaps,
    );
  });

  const instances = new Map<
    string,
    { definition: UxPatternDefinition; path: string }
  >();
  app.uxInstances.forEach((instance, index) => {
    const path = `/applicationBlueprint/uxInstances/${index}`;
    if (instances.has(instance.uxInstanceId)) {
      gaps.push(
        gap("duplicate-ux-instance-id", path, "UX instance IDs must be unique"),
      );
      return;
    }
    const definition = definitions.get(instance.definitionRef);
    if (!definition) {
      gaps.push(
        gap(
          "ux-pattern-not-admitted",
          path,
          "UX instance must reference an exactly admitted definition",
        ),
      );
      return;
    }
    instances.set(instance.uxInstanceId, { definition, path });
  });

  evaluateApplicationAuthority(app, authority, gaps);
  evaluateApplicationRuntimeCapabilities(app, input.capabilityRequirement, gaps);
  evaluateApplicationResourceIds(app, gaps);
  evaluateApplicationPorts(app, gaps);
  evaluateBindings(app, instances, gaps);
  evaluateJourney(app, instances, gaps);
  return dedupe(gaps);
}

/**
 * Prove that the admitted request, the static Blueprint declaration, and the
 * exactly resolvable application resources agree before anything compiles.
 * Every failure is fail-closed: a rejection, never a warning.
 */
function evaluateApplicationRuntimeCapabilities(
  app: ApplicationBlueprint,
  requirement: ApplicationCapabilityRequirement,
  gaps: LibraryGap[],
): void {
  const declarationPath = "/applicationBlueprint/runtimeCapabilities";
  if (!validateApplicationBlueprintRuntimeCapabilities(app.runtimeCapabilities).ok) {
    gaps.push(
      gap(
        "application-webmcp-capability-unavailable",
        declarationPath,
        "Application Blueprint runtime capabilities must satisfy the closed capability contract",
      ),
    );
    return;
  }

  const webMcp = app.runtimeCapabilities.webMcp;
  if (webMcp.mode !== requirement.requestedWebMcpMode) {
    gaps.push(
      gap(
        "application-capability-mismatch",
        "/capabilityRequirement/requestedWebMcpMode",
        "The admitted request mode must equal the selected Application Blueprint mode",
        [requirement.requestedWebMcpMode, webMcp.mode],
      ),
    );
    return;
  }

  const evidenceRefs = requirement.requirementEvidenceRefs;
  const evidencePath = "/capabilityRequirement/requirementEvidenceRefs";
  const evidenceIsWellFormed =
    evidenceRefs.every(
      (ref) =>
        ref.trim().length > 0 &&
        ref.length <= MAX_REQUIREMENT_EVIDENCE_REF_LENGTH,
    ) && new Set(evidenceRefs).size === evidenceRefs.length;
  if (!evidenceIsWellFormed) {
    gaps.push(
      gap(
        "application-webmcp-capability-unavailable",
        evidencePath,
        "Requirement evidence references must be unique, non-blank, and bounded",
      ),
    );
  } else if ((webMcp.mode !== "disabled") !== evidenceRefs.length > 0) {
    gaps.push(
      gap(
        "application-webmcp-capability-unavailable",
        evidencePath,
        webMcp.mode === "disabled"
          ? "A disabled application may not carry requirement evidence"
          : "An enabled application WebMCP mode requires admitted requirement evidence",
      ),
    );
  }

  const datasourceIds = new Set(
    app.datasourceRequirements.map((datasource) => datasource.datasourceId),
  );
  const flowIds = new Set(app.applicationFlows.map((flow) => flow.flowId));
  const seen = new Set<string>();
  webMcp.toolRefs.forEach((toolRef, index) => {
    const path = `${declarationPath}/webMcp/toolRefs/${index}`;
    if (seen.has(toolRef)) {
      gaps.push(
        gap(
          "application-webmcp-capability-unavailable",
          path,
          "Application WebMCP tool references must be unique",
          [toolRef],
        ),
      );
      return;
    }
    seen.add(toolRef);
    const parsed = parseApplicationWebMcpToolRef(toolRef);
    if (!parsed) {
      gaps.push(
        gap(
          "application-webmcp-capability-unavailable",
          path,
          "Application WebMCP tool references must be exactly datasource:<id> or flow:<id>",
          [toolRef],
        ),
      );
      return;
    }
    const resolved =
      parsed.kind === "datasource"
        ? datasourceIds.has(parsed.localId)
        : flowIds.has(parsed.localId);
    if (!resolved) {
      gaps.push(
        gap(
          "application-webmcp-capability-unavailable",
          path,
          parsed.kind === "datasource"
            ? "A datasource tool reference must resolve exactly against an admitted datasource requirement"
            : "A flow tool reference must resolve exactly against an admitted application flow",
          [toolRef],
        ),
      );
    }
  });
}

function evaluateApplicationResourceIds(
  app: ApplicationBlueprint,
  gaps: LibraryGap[],
) {
  const applicationEvents = new Set(app.applicationEvents);
  const capabilities = new Set(app.capabilities);
  const flowIds = new Set<string>();
  app.applicationFlows.forEach((flow, index) => {
    if (flowIds.has(flow.flowId)) {
      gaps.push(
        gap(
          "exact-namespace-collision",
          `/applicationBlueprint/applicationFlows/${index}/flowId`,
          "Application flow IDs must be unique in the exact application namespace",
          [flow.flowId],
        ),
      );
    } else {
      flowIds.add(flow.flowId);
    }
    if (!applicationEvents.has(flow.eventRef)) {
      gaps.push(
        gap(
          "library-event-invalid",
          `/applicationBlueprint/applicationFlows/${index}/eventRef`,
          "Application flow eventRef must resolve to an exact application event",
          [flow.eventRef],
        ),
      );
    }
    if (!capabilities.has(flow.capabilityRef)) {
      gaps.push(
        gap(
          "library-capability-invalid",
          `/applicationBlueprint/applicationFlows/${index}/capabilityRef`,
          "Application flow capabilityRef must resolve to an exact application capability",
          [flow.capabilityRef],
        ),
      );
    }
  });

  const datasourceIds = new Set<string>();
  app.datasourceRequirements.forEach((datasource, index) => {
    if (datasourceIds.has(datasource.datasourceId)) {
      gaps.push(
        gap(
          "exact-namespace-collision",
          `/applicationBlueprint/datasourceRequirements/${index}/datasourceId`,
          "Datasource IDs must be unique in the exact application namespace",
          [datasource.datasourceId],
        ),
      );
    } else {
      datasourceIds.add(datasource.datasourceId);
    }
    if (!capabilities.has(datasource.capabilityRef)) {
      gaps.push(
        gap(
          "library-capability-invalid",
          `/applicationBlueprint/datasourceRequirements/${index}/capabilityRef`,
          "Datasource capabilityRef must resolve to an exact application capability",
          [datasource.capabilityRef],
        ),
      );
    }
  });
}

function evaluateUxAuthority(
  definition: UxPatternDefinition,
  path: string,
  authority: LibraryAuthorityContext,
  gaps: LibraryGap[],
) {
  if (definition.lineage.sourceKind === "project-validation-contract") {
    if (
      !isProjectAuthorityContext(authority) ||
      definition.lineage.authorityStatus !== "project-validation" ||
      definition.lineage.sourceRefs.length !== 1 ||
      definition.lineage.sourceRefs[0] !==
        authority.approvedProjectValidationContractRefs[0]
    ) {
      gaps.push(
        gap(
          "ux-pattern-not-admitted",
          path,
          "Project UX lineage must match the exact process-local contract authority",
        ),
      );
    }
    return;
  }
  if (isProjectAuthorityContext(authority)) {
    gaps.push(
      gap(
        "library-lineage-missing",
        path,
        "Canonical UX lineage may not use project-validation authority",
      ),
    );
    return;
  }
  const admitted =
    definition.lineage.sourceKind === "admitted-ux-research"
      ? authority.admittedUxResearchRefs
      : definition.lineage.sourceKind === "human-authored-canonical"
        ? authority.approvedCanonicalSourceRefs
        : [];
  const admittedSet = new Set(admitted);
  if (
    admitted.length === 0 ||
    definition.lineage.sourceRefs.some((ref) => !admittedSet.has(ref))
  ) {
    gaps.push(
      gap(
        "ux-pattern-not-admitted",
        path,
        "UX lineage refs must be exact members of independent authority",
      ),
    );
  }
}

function evaluateApplicationAuthority(
  app: ApplicationBlueprint,
  authority: LibraryAuthorityContext,
  gaps: LibraryGap[],
) {
  if (app.lineage.sourceKind === "project-validation-contract") {
    const exactProjectAuthority =
      isProjectAuthorityContext(authority) &&
      app.lineage.authorityStatus === "project-validation" &&
      app.applicationCoreRef ===
        authority.approvedProjectValidationCoreRefs[0] &&
      app.lineage.sourceRefs.length === 1 &&
      app.lineage.sourceRefs[0] ===
        authority.approvedProjectValidationContractRefs[0];
    if (!exactProjectAuthority) {
      gaps.push(
        gap(
          "application-blueprint-not-admitted",
          "/applicationBlueprint/lineage",
          "Project Application lineage, core, and contract authority must match exactly",
        ),
      );
    }
    return;
  }
  if (isProjectAuthorityContext(authority)) {
    gaps.push(
      gap(
        "library-lineage-missing",
        "/applicationBlueprint/lineage",
        "Canonical Application lineage may not use project-validation authority",
      ),
    );
    return;
  }
  const coreAdmitted = authority.admittedApplicationCoreRefs.includes(
    app.applicationCoreRef,
  );
  const exactCoreLineage =
    app.lineage.sourceKind === "application-core" &&
    app.lineage.sourceRefs.length === 1 &&
    app.lineage.sourceRefs[0] === app.applicationCoreRef;
  const exactCanonicalLineage =
    app.lineage.sourceKind === "human-authored-canonical" &&
    app.lineage.sourceRefs.every((ref) =>
      authority.approvedCanonicalSourceRefs.includes(ref),
    );
  if (!coreAdmitted || (!exactCoreLineage && !exactCanonicalLineage)) {
    gaps.push(
      gap(
        "application-blueprint-not-admitted",
        "/applicationBlueprint/lineage",
        "Application lineage and Application Core must match independent authority exactly",
      ),
    );
  }
}

function evaluateDefinition(
  definition: UxPatternDefinition,
  path: string,
  gaps: LibraryGap[],
) {
  const requiredCapabilities = new Set(definition.requiredCapabilities);
  const stateIds = new Set<string>();
  const transitionIds = new Set<string>();
  const nodeIds = new Set<string>();
  const portIds = new Set<string>();
  definition.states.forEach((state, index) =>
    addUnique(
      stateIds,
      state.stateId,
      "exact-namespace-collision",
      `${path}/states/${index}/stateId`,
      gaps,
    ),
  );
  definition.transitions.forEach((transition, index) =>
    addUnique(
      transitionIds,
      transition.transitionId,
      "exact-namespace-collision",
      `${path}/transitions/${index}/transitionId`,
      gaps,
    ),
  );
  definition.semanticNodes.forEach((node, index) =>
    addUnique(
      nodeIds,
      node.nodeId,
      "exact-namespace-collision",
      `${path}/semanticNodes/${index}/nodeId`,
      gaps,
    ),
  );
  definition.ports.forEach((port, index) =>
    addUnique(
      portIds,
      port.portId,
      "duplicate-library-port-id",
      `${path}/ports/${index}/portId`,
      gaps,
    ),
  );

  definition.transitions.forEach((transition, index) => {
    const transitionPath = `${path}/transitions/${index}`;
    if (
      !stateIds.has(transition.fromState) ||
      !stateIds.has(transition.toState)
    ) {
      gaps.push(
        gap(
          "ux-pattern-transition-invalid",
          transitionPath,
          "Transition states must resolve exactly in the local definition",
        ),
      );
    }
    const event = definition.ports.find(
      (port) => port.portId === transition.eventRef,
    );
    if (!event || event.kind !== "event" || event.direction !== "in") {
      gaps.push(
        gap(
          "ux-pattern-transition-invalid",
          `${transitionPath}/eventRef`,
          "Transition eventRef must resolve to an event/in port",
        ),
      );
    }
    for (const eventRef of transition.emitEventRefs) {
      const emitted = definition.ports.find((port) => port.portId === eventRef);
      if (!emitted || emitted.kind !== "event" || emitted.direction !== "out") {
        gaps.push(
          gap(
            "ux-pattern-transition-invalid",
            `${transitionPath}/emitEventRefs`,
            "Emitted events must resolve to event/out ports",
          ),
        );
      }
    }
  });
  definition.semanticNodes.forEach((node, index) => {
    if (node.visibleIn.some((stateId) => !stateIds.has(stateId))) {
      gaps.push(
        gap(
          "ux-pattern-state-invalid",
          `${path}/semanticNodes/${index}/visibleIn`,
          "visibleIn states must resolve exactly in the local definition",
        ),
      );
    }
    if (
      node.action &&
      !requiredCapabilities.has(node.action.capabilityRef)
    ) {
      gaps.push(
        gap(
          "library-capability-invalid",
          `${path}/semanticNodes/${index}/action/capabilityRef`,
          "Semantic action capabilityRef must resolve to an exact required capability",
          [node.action.capabilityRef],
        ),
      );
    }
  });
}

function evaluateApplicationPorts(
  app: ApplicationBlueprint,
  gaps: LibraryGap[],
) {
  const ids = new Set<string>();
  const applicationEvents = new Set(app.applicationEvents);
  app.applicationPorts.forEach((port, index) => {
    addUnique(
      ids,
      port.portId,
      "duplicate-library-port-id",
      `/applicationBlueprint/applicationPorts/${index}/portId`,
      gaps,
    );
    if (port.kind === "event" && !applicationEvents.has(port.portId)) {
      gaps.push(
        gap(
          "library-event-invalid",
          `/applicationBlueprint/applicationPorts/${index}/portId`,
          "Application event portId must resolve to an exact application event",
          [port.portId],
        ),
      );
    }
  });
}

function evaluateBindings(
  app: ApplicationBlueprint,
  instances: Map<string, { definition: UxPatternDefinition; path: string }>,
  gaps: LibraryGap[],
) {
  const bindingIds = new Set<string>();
  const boundPorts = new Map<string, number>();
  app.bindings.forEach((binding, index) => {
    const path = `/applicationBlueprint/bindings/${index}`;
    addUnique(
      bindingIds,
      binding.bindingId,
      "exact-namespace-collision",
      `${path}/bindingId`,
      gaps,
    );
    const source = resolveEndpoint(binding.source, app, instances);
    const target = resolveEndpoint(binding.target, app, instances);
    if (!source || !target) {
      gaps.push(
        gap(
          "library-input-schema-invalid",
          path,
          "Binding endpoints must resolve exactly",
        ),
      );
      return;
    }
    if (source.port.direction !== "out" || target.port.direction !== "in") {
      gaps.push(
        gap(
          "incompatible-pattern-port",
          path,
          "Binding direction must be out to in",
        ),
      );
    }
    if (
      source.port.kind !== target.port.kind ||
      source.port.valueType !== target.port.valueType
    ) {
      gaps.push(
        gap(
          "incompatible-pattern-port",
          path,
          "Binding port kind and valueType must match exactly",
        ),
      );
    }
    increment(boundPorts, source.key);
    increment(boundPorts, target.key);
  });
  for (const port of requiredPorts(app, instances)) {
    if (boundPorts.get(port.key) !== 1) {
      gaps.push(
        gap(
          "unbound-pattern-port",
          port.path,
          "Every required port must participate in exactly one binding",
        ),
      );
    }
  }
}

function evaluateJourney(
  app: ApplicationBlueprint,
  instances: Map<string, { definition: UxPatternDefinition; path: string }>,
  gaps: LibraryGap[],
) {
  const journey = app.journey;
  const steps = new Map<string, (typeof journey.steps)[number]>();
  journey.steps.forEach((step, index) => {
    if (steps.has(step.stepId))
      gaps.push(
        gap(
          "exact-namespace-collision",
          `/applicationBlueprint/journey/steps/${index}/stepId`,
          "Journey step IDs must be unique",
        ),
      );
    else steps.set(step.stepId, step);
    step.uxInstanceIds.forEach((instanceId) => {
      if (!instances.has(instanceId))
        gaps.push(
          gap(
            "ux-pattern-not-admitted",
            `/applicationBlueprint/journey/steps/${index}/uxInstanceIds`,
            "Journey instance must resolve exactly",
          ),
        );
    });
  });
  const ownedInstanceIds = new Set(
    journey.steps.flatMap((step) => step.uxInstanceIds),
  );
  for (const [instanceId, instance] of instances) {
    if (!ownedInstanceIds.has(instanceId)) {
      gaps.push(
        gap(
          "ux-pattern-not-admitted",
          instance.path,
          "Every UX instance must be owned by at least one journey step",
        ),
      );
    }
  }
  if (!steps.has(journey.entryStepId))
    gaps.push(
      gap(
        "journey-entry-invalid",
        "/applicationBlueprint/journey/entryStepId",
        "Entry step must exist",
      ),
    );
  for (const terminalId of journey.terminalStepIds) {
    if (!steps.has(terminalId))
      gaps.push(
        gap(
          "journey-terminal-missing",
          "/applicationBlueprint/journey/terminalStepIds",
          "Terminal step must exist",
        ),
      );
  }
  for (const [stepId, step] of steps) {
    for (const transition of step.transitions) {
      const transitionPath = `/applicationBlueprint/journey/steps/${stepId}/transitions`;
      const targetExists = steps.has(transition.toStepId);
      if (!app.applicationEvents.includes(transition.eventRef))
        gaps.push(
          gap(
            "ux-pattern-transition-invalid",
            transitionPath,
            "Journey eventRef must resolve to an application event",
          ),
        );
      if (!targetExists)
        gaps.push(
          gap(
            transition.path === "recovery"
              ? "journey-recovery-invalid"
              : "journey-step-unreachable",
            transitionPath,
            "Journey transition target must exist",
          ),
        );
    }
  }
  const reachableWithoutRecovery = traverseJourney(
    steps,
    journey.entryStepId,
    false,
  );
  const reachable = traverseJourney(steps, journey.entryStepId, true);
  for (const [stepId, step] of steps) {
    if (!reachable.has(stepId))
      gaps.push(
        gap(
          "journey-step-unreachable",
          `/applicationBlueprint/journey/steps/${stepId}`,
          "Journey step must be reachable from entry",
        ),
      );
    for (const transition of step.transitions) {
      if (
        transition.path === "recovery" &&
        (!reachableWithoutRecovery.has(stepId) ||
          !hasNonRecoveryResolution(
            steps,
            transition.toStepId,
            reachableWithoutRecovery,
            journey.terminalStepIds,
          ))
      )
        gaps.push(
          gap(
            "journey-recovery-invalid",
            `/applicationBlueprint/journey/steps/${stepId}/transitions`,
            "Recovery transitions must return to a reachable step",
          ),
        );
    }
  }
  for (const terminalId of journey.terminalStepIds)
    if (!reachable.has(terminalId))
      gaps.push(
        gap(
          "journey-terminal-missing",
          "/applicationBlueprint/journey/terminalStepIds",
          "Terminal step must be reachable",
        ),
      );
}

function resolveEndpoint(
  endpoint: LibraryPortEndpoint,
  app: ApplicationBlueprint,
  instances: Map<string, { definition: UxPatternDefinition; path: string }>,
) {
  if (endpoint.scope === "application") {
    const port = app.applicationPorts.find(
      (candidate) => candidate.portId === endpoint.portId,
    );
    return port ? { port, key: `application:${port.portId}` } : undefined;
  }
  const instance = endpoint.uxInstanceId
    ? instances.get(endpoint.uxInstanceId)
    : undefined;
  const port = instance?.definition.ports.find(
    (candidate) => candidate.portId === endpoint.portId,
  );
  return port && endpoint.uxInstanceId
    ? { port, key: `ux-instance:${endpoint.uxInstanceId}:${port.portId}` }
    : undefined;
}

function requiredPorts(
  app: ApplicationBlueprint,
  instances: Map<string, { definition: UxPatternDefinition; path: string }>,
) {
  const ports: Array<{ port: LibraryPort; key: string; path: string }> =
    app.applicationPorts.map((port, index) => ({
      port,
      key: `application:${port.portId}`,
      path: `/applicationBlueprint/applicationPorts/${index}`,
    }));
  for (const [instanceId, instance] of instances)
    instance.definition.ports.forEach((port, index) =>
      ports.push({
        port,
        key: `ux-instance:${instanceId}:${port.portId}`,
        path: `${instance.path}/ports/${index}`,
      }),
    );
  return ports.filter(({ port }) => port.required);
}

function mapSchemaErrors(
  errors: ErrorObject[] | null | undefined,
  prefix: string,
): LibraryGap[] {
  return (errors ?? []).map((error) => {
    const property = String(
      (error.params as { additionalProperty?: string }).additionalProperty ??
        "",
    );
    const path = `${prefix}${error.instancePath}${property ? `/${property}` : ""}`;
    // Any structural violation inside the capability declaration is a closed
    // capability failure, not a generic schema failure.
    if (path.startsWith("/applicationBlueprint/runtimeCapabilities")) {
      return gap(
        "application-webmcp-capability-unavailable",
        path,
        error.message ?? "Schema validation failed",
      );
    }
    const source =
      /lineage\/(sourceKind|sourceRefs)|\/lineage$/.test(path) &&
      (error.keyword === "enum" || error.keyword === "pattern");
    const presentation =
      error.keyword === "additionalProperties" &&
      isPresentationProperty(property);
    return gap(
      source
        ? "library-source-forbidden"
        : presentation
          ? "library-presentation-forbidden"
          : "library-input-schema-invalid",
      path,
      error.message ?? "Schema validation failed",
    );
  });
}

function isPresentationProperty(property: string): boolean {
  return (
    /class|style|color|coordinate|position|width|height|renderer|figma|padding|spacing|typography/i.test(
      property,
    ) || /^(?:x|y|dx|dy)$/i.test(property)
  );
}

function forEachEndpoint(
  value: unknown,
  callback: (field: unknown, path: string) => void,
) {
  if (!isRecord(value) || !Array.isArray(value.bindings)) return;
  value.bindings.forEach((binding, index) => {
    if (!isRecord(binding)) return;
    forEachEndpointField(
      binding.source,
      `/applicationBlueprint/bindings/${index}/source`,
      callback,
    );
    forEachEndpointField(
      binding.target,
      `/applicationBlueprint/bindings/${index}/target`,
      callback,
    );
  });
}

function forEachEndpointField(
  endpoint: unknown,
  path: string,
  callback: (field: unknown, path: string) => void,
) {
  if (!isRecord(endpoint)) return;
  for (const [key, value] of Object.entries(endpoint))
    if (typeof value === "string") callback(value, `${path}/${key}`);
}

function traverseJourney(
  steps: Map<string, ApplicationBlueprint["journey"]["steps"][number]>,
  entryStepId: string,
  includeRecovery: boolean,
) {
  const reachable = new Set<string>();
  if (!steps.has(entryStepId)) return reachable;
  const queue = [entryStepId];
  while (queue.length) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);
    for (const transition of steps.get(current)!.transitions)
      if (
        (includeRecovery || transition.path !== "recovery") &&
        steps.has(transition.toStepId)
      )
        queue.push(transition.toStepId);
  }
  return reachable;
}

function hasNonRecoveryResolution(
  steps: Map<string, ApplicationBlueprint["journey"]["steps"][number]>,
  recoveryTargetId: string,
  independentlyReachable: Set<string>,
  terminalStepIds: string[],
) {
  const target = steps.get(recoveryTargetId);
  if (!target) return false;
  if (
    independentlyReachable.has(recoveryTargetId) ||
    terminalStepIds.includes(recoveryTargetId)
  ) {
    return true;
  }
  const queue = target.transitions
    .filter((transition) => transition.path !== "recovery")
    .map((transition) => transition.toStepId);
  const visited = new Set<string>();
  while (queue.length) {
    const stepId = queue.shift()!;
    if (visited.has(stepId)) continue;
    visited.add(stepId);
    if (independentlyReachable.has(stepId) || terminalStepIds.includes(stepId)) {
      return true;
    }
    const step = steps.get(stepId);
    if (!step) continue;
    for (const transition of step.transitions)
      if (transition.path !== "recovery") queue.push(transition.toStepId);
  }
  return false;
}

function hasLocalReference(value: unknown) {
  return (
    typeof value === "string" &&
    (value.startsWith("local.") || value.includes(".local."))
  );
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function increment(counts: Map<string, number>, key: string) {
  counts.set(key, (counts.get(key) ?? 0) + 1);
}
function addUnique(
  seen: Set<string>,
  value: string,
  code: LibraryGapCode,
  path: string,
  gaps: LibraryGap[],
) {
  if (seen.has(value))
    gaps.push(
      gap(
        code,
        path,
        "Identifiers must be unique within their exact namespace",
      ),
    );
  else seen.add(value);
}
function gap(
  code: LibraryGapCode,
  path: string,
  message: string,
  evidenceRefs: string[] = [],
): LibraryGap {
  return { code, path, message, evidenceRefs };
}
function dedupe(gaps: LibraryGap[]) {
  return [
    ...new Map(
      gaps.map((item) => [`${item.code}:${item.path}:${item.message}`, item]),
    ).values(),
  ];
}
